import { useEffect, useRef, useState } from 'react';
import Form, { Item as FormItem, GroupItem } from 'devextreme-react/form';
import DataGrid, {
    Column,
    Toolbar,
    Item,
    Lookup,
    MasterDetail
} from 'devextreme-react/data-grid';
import TcmCostEstimateDetails from '../TcmCostEstimateDetails/TcmCostEstimateDetails';
import { BaseButton } from '../../components/BaseButton/BaseButton';
import { Button, Popup } from 'devextreme-react';
import { FormSelectBox } from '../../components/FormSelectBox/FormSelectBox';
import notify from 'devextreme/ui/notify';
import InfoHeader from '../components/InfoHeader';
import { FormTextBox } from '../../components/FormTextBox/FormTextBox';
import TcmCostEstimateDetailsMasterDetail from './TcmCostEstimateDetailsMasterDetail';
import {
    TCM_ROLE_IDS,
    TCM_STATUS
} from '../../WorkflowTasks/tcmWorkflowRules';

import {
    submitNewToDraft,
    submitDraftToReview,
    submitReviewToFinal,
    restartReviewToDraft
} from '../../WorkflowTasks/tcmWorkflowService';
import { useAuth } from "../../hooks/useAuth";

const TcmCostEstimates = ({
    data
    , editing
    , updateField
    , estimateTypes
    , statuses
    , priceBooks
    , onReload
}) => {
    // console.log("data?", data)
    const estimateGridRef = useRef(null);
    const [estimates, setEstimates] = useState([]);
    const [popupVisible, setPopupVisible] = useState(false);
    const [editingEstimate, setEditingEstimate] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [isNew, setIsNew] = useState(false);
    const [submittingEstimateId, setSubmittingEstimateId] = useState(null);
    const tempIdRef = useRef(-1); // for client-side temporary Ids
    const { user } = useAuth();
    const isESSUser = user?.role?.RoleId === TCM_ROLE_IDS.ESS;
    const isBAUser = user?.role?.RoleId === TCM_ROLE_IDS.BA;

    const getEstimateStatus = (estimate) => {
        return estimate?.TCMCostEstimateStatus?.Name;
    }

    // Keep local estimates in sync with project data
    useEffect(() => {
        setEstimates(data?.TCMCostEstimates || []);
    }, [data?.TCMCostEstimates]);


    const commitEstimatesToProject = (newEstimates) => {
        setEstimates(newEstimates);
        // Push back to the TCMProject using the provided updateField helper
        updateField('TCMCostEstimates', newEstimates);
    };
    const isNullOrEmptyEstimateType = (val) =>
        val === null ||
        val === undefined ||
        val === '' ||
        val === 0; // treat 0 (default) as "not selected"

    const handleSaveEstimate = () => {
        if (!editingEstimate) {
            handleCancelPopup();
            // console.log("no 'selected' estimate?")
            return;
        }
        // ---- REQUIRED: EstimateTypeId ----
        if (isNullOrEmptyEstimateType(editingEstimate.EstimateTypeId)) {
            notify(
                'Estimate Type is required. Please select an Estimate Type before saving.',
                'warning',
                4000
            );
            return;
        }
        // Business rule: an estimate must have at least one detail line
        const details = editingEstimate.TCMCostEstimateDetails || [];
        if (details.length === 0) {
            notify(
                'You must add at least one line item before saving this estimate.',
                'warning',
                4000
            );
            return;
        }

        let newEstimates;
        if (isNew) {
            newEstimates = [...estimates, editingEstimate];
        } else {
            newEstimates = estimates.map((e, idx) =>
                idx === editingIndex ? editingEstimate : e
            );
        }

        commitEstimatesToProject(newEstimates);
        handleCancelPopup();
    };

    const handleDetailsChanged = (updatedEstimateWithDetails) => {
        // Called from TcmCostEstimateDetails whenever the details grid changes
        setEditingEstimate(updatedEstimateWithDetails);
    };
    const handleAddEstimate = () => {
        const newEstimate = {
            Id: tempIdRef.current--,
            StatusId: 1,
            ProjectId: data?.Id || 0,
            EstimateTypeId: 0,
            ClonedEstimateId: 0,
            Title: '',
            OMRate: 0,
            MiscTotal: 0,
            Comments: '',
            // CreatedBy: data?.CreatedBy || '',
            // CreatedDate: data?.CreatedDate || null,
            // UpdatedBy: data?.UpdatedBy || null,
            // UpdatedDate: data?.UpdatedDate || null,
            TCMCostEstimateDetails: []
        };

        setEditingEstimate(newEstimate);
        setEditingIndex(null);
        setIsNew(true);
        setPopupVisible(true);
    };

    const handleEditEstimate = (rowData, rowIndex) => {
        // Deep copy to allow cancel without mutating the grid row until save
        const copy = JSON.parse(JSON.stringify(rowData));
        setEditingEstimate(copy);
        setEditingIndex(rowIndex);
        setIsNew(false);
        setPopupVisible(true);
    };

    const handleCloneEstimate = (rowData, rowIndex) => {
        // Deep copy to avoid mutating the original row
        const original = JSON.parse(JSON.stringify(rowData));

        // Safely clone and update the details
        const clonedDetails = (original.TCMCostEstimateDetails || []).map(detail => ({
            ...detail,
            // Add "_copy" to each ItemDescription
            ItemDescription: detail.ItemDescription
                ? `copy_${detail.ItemDescription}`
                : 'copy'
        }));

        const cloned = {
            ...original,
            // Give this clone a new temporary Id so the backend treats it as new
            Id: tempIdRef.current--,
            // Update the title so users can see it is a copy
            Title: original.Title ? `copy_${original.Title}` : 'Copy',
            // Track which estimate it was cloned from
            ClonedEstimateId: original.Id,
            // Reset status to "new"
            StatusId: 1,
            // Use the updated details
            TCMCostEstimateDetails: clonedDetails
        };

        // Treat this as a new estimate so handleSaveEstimate will append it
        setEditingEstimate(cloned);
        setEditingIndex(null);  // do not tie it to the original row index
        setIsNew(true);         // tells handleSaveEstimate to push instead of replace
        setPopupVisible(true);
    };


    const handleCancelPopup = () => {
        setPopupVisible(false);
        setEditingEstimate(null);
        setEditingIndex(null);
        setIsNew(false);
    };
    const handleEstimateFieldChange = (field, value) => {
        setEditingEstimate(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const computeEquipmentTotal = (details) => {
        if (!details) return 0;
        return details.reduce((sum, item) => {
            const unitPrice = Number(item.UnitPrice) || 0;
            const quantity = Number(item.Quantity) || 0;
            const value = sum + unitPrice * quantity;
            return Number(value.toFixed(2));
        }, 0);
    };

    const computeLaborTotal = (details) => {
        if (!details) return 0;
        return details.reduce((sum, item) => {
            const laborPrice = Number(item.LaborPrice) || 0;
            const laborHours = Number(item.LaborHours) || 0;
            const value = sum + laborPrice * laborHours;
            return Number(value.toFixed(2));
        }, 0);
    };

    const computeGrandTotal = (estimate) => {
        if (!estimate) return 0;

        const details = estimate.TCMCostEstimateDetails || [];
        const equipmentTotal = computeEquipmentTotal(details);
        const laborTotal = computeLaborTotal(details);
        const omRate = Number(estimate.OMRate) || 0; // decimal, e.g. 0.6 for 60%

        const baseTotal = equipmentTotal + laborTotal;
        const value = baseTotal * (1 + omRate);
        return Number(value.toFixed(2));
    };

    const formatCurrency = (value) =>
        value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        });

    const canSubmitEstimate = (estimate) => {
        const status = getEstimateStatus(estimate);

        return (
            (isESSUser && status === TCM_STATUS.NEW) ||
            (isBAUser && status === TCM_STATUS.DRAFT) ||
            (isESSUser && status === TCM_STATUS.REVIEW)
        );
    }
    const canCloneEstimate = (estimate) => {

        /**
         * Below is the proper code to allow this estimate to be cloned, 
         * but it seems that the ESS user or BA (which doesn't exist) is not 
         * set up correctly to even access the page. This is out of scope of this ticket
         * so I've just returned true always until we work this logic out on the navigation.
         */
        // const status = getEstimateStatus(estimate);
        //
        // return (
        //     (isESSUser && status === TCM_STATUS.NEW) ||
        //     (isBAUser && status === TCM_STATUS.DRAFT) ||
        //     (isESSUser && status === TCM_STATUS.REVIEW)
        // );
        return true;
    }
    const canRestartEstimate = (estimate) => {
        const status = getEstimateStatus(estimate);

        return isESSUser && status === TCM_STATUS.REVIEW;
    }

    const getSubmitButtonText = (estimate) => {
        const status = getEstimateStatus(estimate);

        if (isBAUser && status === TCM_STATUS.DRAFT) {
            return 'Review';
        }

        if (isESSUser && status === TCM_STATUS.REVIEW) {
            return 'Finalize';
        }

        return 'Submit';
    };

    const getSubmitAction = (estimate) => {
        const status = getEstimateStatus(estimate);

        if (isESSUser && status === TCM_STATUS.NEW) {
            return {
                updateFn: submitNewToDraft,
                successMessage: `Cost Estimate ${estimate.Id} submitted to Draft.`
            };
        }

        if (isBAUser && status === TCM_STATUS.DRAFT) {
            return {
                updateFn: submitDraftToReview,
                successMessage: `Cost Estimate ${estimate.Id} submitted for Review.`
            };
        }

        if (isESSUser && status === TCM_STATUS.REVIEW) {
            return {
                updateFn: submitReviewToFinal,
                successMessage: `Cost Estimate ${estimate.Id} submitted to Final.`
            };
        }

        return null;
    };

    const handleWorkflowStatusUpdate = async (estimate, action) => {
        if (!estimate?.Id || !action?.updateFn) return;

        setSubmittingEstimateId(estimate.Id);

        try {
            const result = await action.updateFn(estimate.Id);

            if (result.Success) {
                window.location.reload(); //TODO: replace this call with onReload function
                notify(action.successMessage, 'success', 4000);
                // await onReload?.();

                return;
            }

            notify(
                result.Errors?.[0]?.Message ?? 'Failed to update Cost Estimate status.',
                'error',
                5000
            );
        } catch {
            notify(
                'An error occurred while updating the Cost Estimate status.',
                'error',
                5000
            );
        } finally {
            setSubmittingEstimateId(null);
        }
    };

    const handleSubmitEstimate = async (estimate) => {
        const action = getSubmitAction(estimate);
        await handleWorkflowStatusUpdate(estimate, action);
    };

    const handleRestartEstimate = async (estimate) => {
        await handleWorkflowStatusUpdate(estimate, {
            updateFn: restartReviewToDraft,
            successMessage: `Cost Estimate ${estimate.Id} restarted and sent back to Draft.`
        });
    };

    return (
        // {/* Main TCM Cost Estimate Grid */}
        <>
            <DataGrid
                id='tcmCostEstimatesDataGrid'
                keyExpr="Id"
                dataSource={estimates}
                showBorders={true}
                columnAutoWidth={true}
                ref={estimateGridRef}
                noDataText="No cost estimates have been added for this project."
            >
                <Toolbar visible={editing} >
                    <Item location="after">
                        <Button
                            id="add-tcm-cost-estimate-datagrid"
                            icon="add"
                            text="Add Estimate"
                            stylingMode="contained"
                            disabled={!editing}
                            onClick={handleAddEstimate}
                        />
                    </Item>
                </Toolbar>

                <Column dataField="Title" caption="Title" />
                {/* Estimate Status.*/}
                <Column dataField="StatusId" caption="Estimate Status" >
                    <Lookup
                        dataSource={statuses}
                        displayExpr="Name"
                        valueExpr="Id"
                    />
                </Column>
                {/* Estimate Type. */}
                <Column dataField="EstimateTypeId" caption="Estimate Type" >
                    <Lookup
                        dataSource={estimateTypes}
                        displayExpr="Name"
                        valueExpr="Id"
                    />
                </Column>
                {/* O&M Rate (Percentage) */}
                <Column
                    dataField="OMRate"
                    caption="O&M Rate"
                    dataType="number"
                    cellRender={({ value }) => {
                        // If value is between 0 and 1, display as percentage
                        if (typeof value === 'number' && value >= 0 && value < 1) {
                            return `${(value * 100).toFixed(2).replace(/\.00$/, '')}%`;
                        }
                        // Otherwise, display as is
                        return value;
                    }}
                />
                {/* Labor Total (Calculated = Labor Hours X Labor Rate - Ready Only) */}
                <Column
                    caption="Labor Total"
                    dataType="number"
                    headerCellRender={() =>
                        <InfoHeader
                            label="Labor Total"
                            tooltip="Sum of (Labor Rate * Labor Hours) for all line items."
                        />
                    }
                    cellRender={({ data }) => {
                        // Calculate Labor Total from TCMCostEstimateDetails
                        const details = data.TCMCostEstimateDetails || [];
                        const laborTotal = details.reduce((sum, item) => {
                            // Ensure both values are numbers
                            const laborPrice = Number(item.LaborPrice) || 0;
                            const laborHours = Number(item.LaborHours) || 0;
                            return sum + (laborPrice * laborHours);
                        }, 0);

                        // Format as US currency
                        return laborTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
                    }}
                />
                {/* Equipment Total (Calculated - Sum of all Unit Total Items) */}
                <Column
                    caption="Equipment Total"
                    dataType="number"
                    headerCellRender={() =>
                        <InfoHeader
                            label="Equipment Total"
                            tooltip="Sum of (Unit Price * Quantity) for all line items."
                        />
                    }
                    cellRender={({ data }) => {
                        const details = data.TCMCostEstimateDetails || [];
                        const equipmentTotal = details.reduce((sum, item) => {
                            const unitPrice = Number(item.UnitPrice) || 0;
                            const quantity = Number(item.Quantity) || 0;
                            return sum + (unitPrice * quantity);
                        }, 0);

                        // Format as US currency
                        return equipmentTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
                    }}
                />

                {/* Total Cost (Calculated = Unit Total + Labor Total per item - Ready Only) with OM Rate tacked on*/}
                <Column
                    caption="Total Cost"
                    dataType="number"
                    headerCellRender={() =>
                        <InfoHeader
                            label="Total Cost"
                            tooltip="(Equipment Total + Labor Total) * (1 + O&M Rate)."
                        />
                    }
                    cellRender={({ data }) => {
                        const details = data.TCMCostEstimateDetails || [];
                        const OMRate = Number(data.OMRate) || 0;

                        // Calculate Equipment Total
                        const equipmentTotal = details.reduce((sum, item) => {
                            const unitPrice = Number(item.UnitPrice) || 0;
                            const quantity = Number(item.Quantity) || 0;
                            return sum + (unitPrice * quantity);
                        }, 0);

                        // Calculate Labor Total
                        const laborTotal = details.reduce((sum, item) => {
                            const laborPrice = Number(item.LaborPrice) || 0;
                            const laborHours = Number(item.LaborHours) || 0;
                            return sum + (laborPrice * laborHours);
                        }, 0);

                        // Calculate Total Cost using your formula
                        const baseTotal = equipmentTotal + laborTotal;
                        const totalCost = baseTotal * (1 + OMRate);

                        // Format as US currency
                        return totalCost.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
                    }}
                />

                <Column dataField="Comments" caption="Comments" />
                <Column
                    caption="Actions"
                    width={300}
                    alignment="center"
                    cellRender={(cell) => {
                        const { rowIndex, data: rowData } = cell;
                        const isSubmittingThisRow = submittingEstimateId === rowData.Id;
                        const submitButtonText = getSubmitButtonText(rowData);

                        return (
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                <Button
                                    icon="edit"
                                    hint="Edit"
                                    disabled={!editing || isSubmittingThisRow}
                                    onClick={() => handleEditEstimate(rowData, rowIndex)}
                                />
                                {canCloneEstimate(rowData) &&
                                    <Button
                                        icon="copy"
                                        hint="Clone"
                                        disabled={!editing || isSubmittingThisRow}
                                        onClick={() => handleCloneEstimate(rowData, rowIndex)}
                                    />
                                }
                                {canSubmitEstimate(rowData) && (
                                    <Button
                                        text={submitButtonText}
                                        type="default"
                                        stylingMode="outlined"
                                        disabled={isSubmittingThisRow}
                                        onClick={() => handleSubmitEstimate(rowData)}
                                    />
                                )}

                                {canRestartEstimate(rowData) && (
                                    <Button
                                        text="Restart"
                                        type="danger"
                                        stylingMode="outlined"
                                        disabled={isSubmittingThisRow}
                                        onClick={() => handleRestartEstimate(rowData)}
                                    />
                                )}
                            </div>
                        );
                    }}
                />
                <MasterDetail
                    enabled={true}
                    render={detailProps => {
                        const estimateRow = detailProps.data;
                        return (
                            <TcmCostEstimateDetailsMasterDetail
                                estimate={estimateRow}
                                editing={editing}
                                priceBooks={priceBooks}
                            // onEstimateChange={(updatedEstimate) => {
                            //     // update estimates array in React state
                            //     const newEstimates = estimates.map(e =>
                            //         e.Id === updatedEstimate.Id ? updatedEstimate : e
                            //     );
                            //     setEstimates(newEstimates);
                            //     // push up to project form via updateField
                            //     updateField('TCMCostEstimates', newEstimates);
                            // }}
                            />
                        );
                    }}
                />
            </DataGrid>
            <Popup
                visible={popupVisible}
                onHiding={handleCancelPopup}
                showCloseButton={true}
                showTitle={true}
                title={'Cost Estimate'}
                // maxWidth={900}
                // minWidth={700}
                // height="80vh"
                dragEnabled={false}
            >
                <Form
                    labelLocation="top"
                    colCount={1}
                    formData={editingEstimate}
                // customizeItem={customizeItem}
                >
                    <GroupItem>
                        <GroupItem colCount={2}>

                            <FormItem
                                dataField="Title"
                                label={{ text: "Title" }}
                                labelMode='static'
                                elementAttr={{ class: 'form-editor' }}
                            />
                            <FormItem dataField='Estimate Type'>
                                <FormSelectBox
                                    // label='Estimate Type'
                                    value={editingEstimate ? editingEstimate.EstimateTypeId : 0}
                                    isEditing={editing}
                                    dataSource={estimateTypes ? estimateTypes : []}
                                    displayExpr={"Name"}
                                    valueExpr={"Id"}
                                    onValueChange={val => handleEstimateFieldChange('EstimateTypeId', val)}
                                    required={true}
                                    searchEnabled={true}
                                    noDataText="No estimate types to display"
                                />
                            </FormItem>
                            <FormItem dataField='Status'>
                                <FormTextBox
                                    value={editingEstimate ? editingEstimate?.TCMCostEstimateStatus?.Name : null}
                                    isEditing={false} // status is not editable in the form, only via workflow actions
                                    onValueChange={() => { }}
                                />
                            </FormItem>
                            <FormItem
                                label={{ text: "O&M Rate (%)" }}  // show as percent to user
                                labelMode="static"
                                elementAttr={{ class: 'form-editor' }}
                                editorType="dxNumberBox"
                                editorOptions={{
                                    min: 0,
                                    max: 100,
                                    showSpinButtons: true,
                                    format: '#0.##',
                                    // Display value as percent (decimal * 100)
                                    value:
                                        editingEstimate && editingEstimate.OMRate != null
                                            ? editingEstimate.OMRate * 100
                                            : null,
                                    onValueChanged: (e) => {
                                        const percent = e.value;
                                        const decimal =
                                            typeof percent === 'number' && !Number.isNaN(percent)
                                                ? percent / 100
                                                : null;

                                        // Store decimal (.6 for 60%)
                                        handleEstimateFieldChange('OMRate', decimal);
                                    }
                                }}
                            />
                            <FormItem
                                dataField="Comments"
                                caption="Comments"
                                labelMode='static'
                                elementAttr={{ class: 'form-editor' }}
                            />

                        </GroupItem>
                        <GroupItem colCount={4} caption="Cost Estimate Totals">
                            {/* Equipment Total (display-only) */}
                            <FormItem
                                labelMode="static"
                                elementAttr={{ class: 'form-editor' }}
                                render={() => (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <InfoHeader
                                                label="Equipment Total"
                                                tooltip="Sum of (Unit Price * Quantity) for all line items."
                                            />
                                        </div>
                                        <FormTextBox
                                            value={formatCurrency(computeEquipmentTotal(editingEstimate?.TCMCostEstimateDetails))}
                                            isEditing={false}
                                            onValueChange={() => { }}
                                        />
                                    </div>
                                )}
                            />

                            {/* Labor Total (display-only) */}
                            <FormItem
                                labelMode="static"
                                elementAttr={{ class: 'form-editor' }}
                                render={() => (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <InfoHeader
                                                label="Labor Total"
                                                tooltip="Sum of (Labor Rate * Labor Hours) for all line items."
                                            />
                                        </div>
                                        <FormTextBox
                                            value={formatCurrency(computeLaborTotal(editingEstimate?.TCMCostEstimateDetails))}
                                            isEditing={false}
                                            onValueChange={() => { }}
                                        />
                                    </div>
                                )}
                            />
                            {/* Grand Total (display-only) */}
                            <FormItem
                                labelMode="static"
                                elementAttr={{ class: 'form-editor' }}
                                render={() => (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <InfoHeader
                                                label={'Total Cost'}
                                                tooltip={"Total Cost = (Equipment Total + Labor Total) * (1 + O&M Rate)"}
                                            />
                                        </div>
                                        <FormTextBox
                                            value={formatCurrency(computeGrandTotal(editingEstimate))}
                                            isEditing={false}
                                            onValueChange={() => { }}
                                        />
                                    </div>
                                )}
                            />
                        </GroupItem>
                    </GroupItem>
                    {/* DETAILS - COST ESTIMATE DATAGRID */}
                    <GroupItem caption="Line Items">
                        <FormItem>
                            {editingEstimate && (
                                <TcmCostEstimateDetails
                                    estimate={editingEstimate}
                                    onChange={handleDetailsChanged}
                                    editing={editing}
                                    priceBooks={priceBooks}
                                />
                            )}

                        </FormItem>
                    </GroupItem>
                </Form>
                {/* Popup actions */}
                <div
                    style={{
                        marginTop: 16,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8
                    }}
                >
                    <BaseButton
                        label="Cancel"
                        onClick={handleCancelPopup}
                        variant="outlined"
                    />
                    <BaseButton
                        label="Save"
                        variant="outlined"
                        onClick={handleSaveEstimate}
                        type="default"
                    />
                </div>
            </Popup >
        </>
    );
};

export default TcmCostEstimates;
